import React, { useMemo } from 'react';
import { Tooltip } from '@bedrock/components';
import { useMemoizedFn } from 'ahooks';
import { cn } from '@mpc/common/src/utils/cn';
import { InputVariant } from './triggers/InputVariant';
import { SelectVariant } from './triggers/SelectVariant';
import { DivVariant } from './triggers/DivVariant';
import type {
  InputTriggerProps,
  TriggerComponent,
  TriggerRegistry,
  TriggerContext,
} from './types';

// ===== 导出类型 =====
export type {
  InputTriggerProps,
  TriggerComponent,
  TriggerRegistry,
  TriggerContext,
} from './types';

// ===== 注册中心 =====

/**
 * 全局触发器注册表。
 * 内置 3 种：input / select / div。
 * 业务方可通过 `registerInputTrigger(name, Component)` 扩展。
 */
const TRIGGER_REGISTRY: TriggerRegistry = {
  input: InputVariant,
  select: SelectVariant,
  div: DivVariant,
};

/**
 * 注册自定义触发器。
 *
 * @example
 * registerInputTrigger('tag', MyTagTrigger)
 * // 然后可以 triggerType="tag"
 */
export function registerInputTrigger(
  name: string,
  component: TriggerComponent,
): void {
  TRIGGER_REGISTRY[name] = component;
}

/**
 * 查找触发器组件（按优先级）。
 *
 * 优先级：
 * 1. ReactElement → clone 注入 onClick/value/displayValue/...
 * 2. Function component → 直接使用
 * 3. triggerType 字符串 → 从注册表查找，未找到回退 select
 */
function resolveTrigger(
  triggerType?: string,
  trigger?: TriggerComponent | React.ReactElement,
): TriggerComponent {
  if (React.isValidElement(trigger)) {
    // React 元素：clone 注入 onClick 等 props
    return ((props: TriggerContext) => {
      const { onClick, value, displayValue, disabled, placeholder } = props;
      return React.cloneElement(trigger as React.ReactElement, {
        onClick,
        value,
        displayValue,
        disabled,
        placeholder,
      });
    }) as unknown as TriggerComponent;
  }

  if (typeof trigger === 'function') {
    return trigger as TriggerComponent;
  }

  // 从注册表查找，默认回退 select
  const type = triggerType ?? 'select';
  return TRIGGER_REGISTRY[type] ?? SelectVariant;
}

// ===== 主组件 =====

/**
 * InputTrigger — 可注册触发器的表单选择组件。
 *
 * 负责「触发器 UI + 值显示 + 点击桥接 + Form 契约」四件事。
 * Modal 完全交给调用方（通过 onTrigger 钩子配合 AddModals / 自定义 Modal）。
 *
 * @example SFormItem + formItemkeyMap（推荐）
 * ```tsx
 * <SFormItem
 *   formItemKey="$project"
 *   label="项目编号"
 *   formItemKeyMap={{
 *     project_number: 'project_number',
 *     before_number: 'before_number',
 *   }}
 *   render={() => (
 *     <InputTrigger
 *       triggerType="select"
 *       placeholder="请选择项目"
 *       getDisplay={(v) => v?.project_number
 *         ? `${v.project_number}${v.before_number ? ' - ' + v.before_number : ''}`
 *         : ''
 *       }
 *       onTrigger={({ value, onConfirm }) => {
 *         AddItemsModal.show({
 *           selectMode: 'single',
 *           items: value ? [value] : [],
 *           onConfirm: (item) => onConfirm(item),
 *         });
 *       }}
 *     />
 *   )}
 * />
 * ```
 *
 * @example 独立使用（非 Form 场景）
 * ```tsx
 * <InputTrigger
 *   triggerType="input"
 *   value={selectedUser}
 *   onChange={setSelectedUser}
 *   getDisplay={(v) => v?.name ?? ''}
 *   placeholder="选择用户"
 *   onTrigger={({ onConfirm }) => {
 *     UserSelectModal.show({ onConfirm });
 *   }}
 * />
 * ```
 */
function InputTrigger<TValue = unknown>({
  value,
  onChange,
  getDisplay = (v: TValue | undefined) => (v == null ? '' : String(v)),
  placeholder = '请选择',
  triggerType,
  trigger,
  triggerProps,
  onTrigger,
  disabled = false,
  tooltip,
  className,
}: InputTriggerProps<TValue>): React.ReactElement {
  const TriggerComp = useMemo(
    () => resolveTrigger(triggerType, trigger),
    [triggerType, trigger],
  );

  const displayValue = useMemo(
    () => getDisplay(value),
    [value, getDisplay],
  );

  const handleClick = useMemoizedFn(() => {
    if (disabled) return;
    onTrigger?.({
      value,
      onConfirm: (next) => {
        onChange?.(next);
      },
    });
  });

  const node = (
    <TriggerComp
      value={value}
      displayValue={displayValue}
      onClick={handleClick}
      disabled={disabled}
      placeholder={placeholder}
      triggerProps={triggerProps}
    />
  );

  const wrapped = className
    ? <span className={cn('inline-block', className)}>{node}</span>
    : node;

  if (tooltip) {
    return <Tooltip title={tooltip as string}>{wrapped}</Tooltip>;
  }

  return wrapped;
}

InputTrigger.displayName = 'InputTrigger';

export default InputTrigger;
